import { BLOCKS } from '@contentful/rich-text-types';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Text,
  Grid,
  GridItem,
  Hide,
  Show,
  Box,
  Button,
} from '@everlywell/ui-kit';
import {
  getMobileAndDesktopFirstImage,
  getMobileAndDesktopFirstVideo,
} from 'components/PageSections/utils';
import RichTextWrapper from 'components/RichTextWrapper';
import VideoComponent, { mergeHtmlVideoProps } from 'components/VideoComponent';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useState, useEffect } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import { notEmpty } from 'utils/types/utilityTypes';

import SectionContainer from '../components/SectionContainer';

export type DigitalResultsSectionProps = {
  data: Queries.pdpSectionFragment;
};

/**
 * This section is used to render the Digital Results section on the product details page.
 */
const DigitalResultsSection = (props: DigitalResultsSectionProps) => {
  const section = props.data;

  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);

  useEffect(() => {
    setShouldPlayVideo(true);
  }, []);

  if (!section) return null;

  const sectionImages = getMobileAndDesktopFirstImage(section.images ?? null);
  const sectionVideos = getMobileAndDesktopFirstVideo(section.images ?? null);
  const videoStyle = {
    loop: true,
    muted: true,
    playsInline: true,
    autoPlay: shouldPlayVideo,
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'left',
    },
  } as const;

  const mobileVideoHtmlProps = sectionVideos.mobileVideo
    ? mergeHtmlVideoProps(sectionVideos.mobileVideo, {
        ...videoStyle,
        poster:
          sectionImages.mobileImage?.gatsbyImageData?.images?.fallback?.src,
      })
    : null;

  const desktopVideoHtmlProps = sectionVideos.desktopVideo
    ? mergeHtmlVideoProps(sectionVideos.desktopVideo, {
        ...videoStyle,
        poster:
          sectionImages?.desktopImage?.gatsbyImageData?.images?.fallback?.src,
      })
    : null;

  const eyebrow = section.eyebrow || section.metadata?.context?.eyebrow;

  const accordion = section.subsections?.[0];
  const accordionSections =
    accordion && 'subsections' in accordion ? accordion.subsections ?? [] : [];

  const cta = section.metadata?.ctas?.[0];
  const ctaUrl = cta?.url?.includes('http')
    ? replaceRedirectionLinkRoot(cta.url)
    : cta?.url ?? '';
  const isInternalLink = cta?.url?.startsWith('/');

  const mobileDirection =
    section.metadata?.flags?.find(
      (flag) => flag?.displayName === 'mobileDirection',
    )?.metadata?.metadata_id || 'column-reverse';

  const trackAccordionClick = (title: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_ACCORDION,
      data: {
        accordionTitle: title || 'No title provided',
        contentfulSection: 'DigitalResultsSection',
      },
    });
  };

  return (
    <SectionContainer size={['sm', 'sm', 'xl']} overflow="clip">
      <Grid
        spacing={[10, 10, 20]}
        flexDirection={['column-reverse', null, 'row']}
      >
        <GridItem
          width={[1, null, 0.5]}
          display={[
            mobileDirection === 'column' ? 'none' : 'flex',
            mobileDirection === 'column' ? 'none' : 'flex',
            'flex',
          ]}
          justifyContent="center"
        >
          {/* render desktop image if it exists */}
          <Box position="sticky">
            {sectionVideos.desktopVideo ? (
              <Box display={['none', null, null, 'flex']} width="100%">
                <VideoComponent
                  {...sectionVideos.desktopVideo}
                  htmlVideoProps={desktopVideoHtmlProps ?? undefined}
                />
              </Box>
            ) : sectionImages.desktopImage ? (
              <Show above="tablet">
                <GatsbyImage
                  image={sectionImages.desktopImage.gatsbyImageData}
                  alt={sectionImages.desktopImage.description ?? ''}
                />
              </Show>
            ) : null}

            {/* render mobile image if it exists */}
            {sectionVideos.mobileVideo ? (
              <Box
                display={['flex', null, null, 'none']}
                height={['360px', 'auto']}
                width="100%"
              >
                <VideoComponent
                  {...sectionVideos.mobileVideo}
                  htmlVideoProps={mobileVideoHtmlProps ?? undefined}
                />
              </Box>
            ) : sectionImages.mobileImage ? (
              <Hide above="tablet">
                <GatsbyImage
                  image={sectionImages.mobileImage.gatsbyImageData}
                  alt={sectionImages.mobileImage.description ?? ''}
                />
              </Hide>
            ) : null}
          </Box>
        </GridItem>

        <GridItem width={[1, null, 0.5]}>
          {eyebrow && (
            <Text
              color="viridian.base"
              paddingBottom={3}
              textAlign={['center', null, 'start']}
            >
              {eyebrow}
            </Text>
          )}

          {section.body?.raw && (
            <Box textAlign={['center', null, 'start']} marginBottom={10}>
              <RichTextWrapper contentfulRichText={section.body} />
            </Box>
          )}

          {/* render mobile image if it exists */}
          {mobileDirection === 'column' ? (
            <Box position="sticky" display={['block', 'block', 'none']}>
              {sectionVideos.desktopVideo ? (
                <Box display={['none', null, null, 'flex']} width="100%">
                  <VideoComponent
                    {...sectionVideos.desktopVideo}
                    htmlVideoProps={desktopVideoHtmlProps ?? undefined}
                  />
                </Box>
              ) : sectionImages.desktopImage ? (
                <Show above="tablet">
                  <GatsbyImage
                    image={sectionImages.desktopImage.gatsbyImageData}
                    alt={sectionImages.desktopImage.description ?? ''}
                  />
                </Show>
              ) : null}

              {/* render mobile image if it exists */}
              {sectionVideos.mobileVideo ? (
                <Box
                  display={['flex', null, null, 'none']}
                  height={['360px', 'auto']}
                  width="100%"
                >
                  <VideoComponent
                    {...sectionVideos.mobileVideo}
                    htmlVideoProps={mobileVideoHtmlProps ?? undefined}
                  />
                </Box>
              ) : sectionImages.mobileImage ? (
                <Hide above="tablet">
                  <GatsbyImage
                    image={sectionImages.mobileImage.gatsbyImageData}
                    alt={sectionImages.mobileImage.description ?? ''}
                  />
                </Hide>
              ) : null}
            </Box>
          ) : null}

          {accordion && (
            <Accordion allowToggle width="100%">
              {accordionSections.map((section, index) => {
                if (!section || !('contentful_id' in section)) {
                  return null;
                }

                // Return null if title doesn't exist
                if (!section.metadata?.context?.title) {
                  return null;
                }

                // If body doesn't exist but title does, render as list
                if (!section.body) {
                  return (
                    <Box
                      key={section.contentful_id}
                      borderBottom="1px solid"
                      borderColor="tints.lightGrey"
                      marginTop={index === 0 ? [8, null, 10] : []}
                      paddingBottom={[4, null, 6]}
                      marginBottom="6"
                    >
                      <Text fontWeight="medium">
                        {section.metadata?.context?.title}
                      </Text>
                    </Box>
                  );
                }

                return (
                  <AccordionItem key={section.contentful_id}>
                    <AccordionButton
                      sx={{ fontWeight: 'medium' }}
                      onClick={() =>
                        trackAccordionClick(
                          section.metadata?.context?.title || '',
                        )
                      }
                    >
                      {section.metadata?.context?.title ?? ''}
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel sx={{ py: 3 }}>
                      <Grid spacing={2}>
                        <GridItem width={1}>
                          <RichTextWrapper
                            contentfulRichText={section.body}
                            options={{
                              references:
                                section.body?.references?.filter(notEmpty) ??
                                [],
                            }}
                          />
                        </GridItem>

                        {section.subsections?.map((subsection) => {
                          if (!subsection || !('contentful_id' in subsection))
                            return null;

                          if (!subsection.body) return null;

                          return (
                            <GridItem
                              width={1 / 3}
                              key={subsection.contentful_id}
                            >
                              <RichTextWrapper
                                contentfulRichText={subsection.body}
                                options={{
                                  references:
                                    subsection.body?.references?.filter(
                                      notEmpty,
                                    ) ?? [],
                                  overrideOptions: {
                                    renderNode: {
                                      [BLOCKS.HEADING_6]: (_, children) => (
                                        <Text
                                          size="lg"
                                          marginBottom={3}
                                          fontWeight="medium"
                                        >
                                          {children}
                                        </Text>
                                      ),
                                      [BLOCKS.PARAGRAPH]: (_, children) => (
                                        <Text size="md" marginBottom={3}>
                                          {children}
                                        </Text>
                                      ),
                                    },
                                  },
                                }}
                              />
                            </GridItem>
                          );
                        })}
                      </Grid>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}

          {cta && cta.label && (
            <Box textAlign={['center', null, 'start']} marginTop={10}>
              <Button
                color={cta?.metadata?.theme?.textColor || 'tints.black'}
                backgroundColor={
                  cta?.metadata?.theme?.backgroundColor || 'none'
                }
                _hover={{
                  backgroundColor:
                    cta?.metadata?.theme?.gradientColor1 || 'none',
                }}
                borderRadius="28px"
                width={['100%', null, 'fit-content']}
                onClick={() => {
                  analytics.track({
                    event: ANALYTICS.EVENTS.CLICKED_BUTTON,
                    data: {
                      ctaText: cta.label || 'No CTA label',
                      contentfulSection: 'DigitalResultsSection',
                    },
                  });
                }}
                {...(!isInternalLink && { as: 'a', href: ctaUrl })}
                {...(isInternalLink && { as: Link, to: ctaUrl })}
              >
                <Text size="lg">{cta.label}</Text>
              </Button>
            </Box>
          )}
        </GridItem>
      </Grid>
    </SectionContainer>
  );
};

export default DigitalResultsSection;
